/**
 * Screen 3: who to book, insurance, and two unchecked, practice-named
 * consents (OB-18). The hold counts down (OB-03); a lapsed hold can be taken
 * again or swapped for the next open times (OB-04). A valid mobile with
 * texting consent is reported as funnel progress so an abandoned visit
 * becomes one lead (OB-19).
 */
import type {
  Attribution,
  BookingFor,
  Hold,
  PublicSite,
  PublicType,
  Slot,
  SubmitRequest,
  SubmitResult,
} from '@velora/contracts/booking';
import { TURNSTILE_ACTIONS } from '@velora/contracts/turnstile';
import { Button, Checkbox, Combobox, Field, Input, Select, Textarea } from '@velora/ui';
import { AlertTriangle, Clock, ShieldCheck } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { Booker } from '../flow.js';
import { alternativesOf, messageOf, reasonOf, type BookingApi } from '../lib/api.js';
import { knowsSource } from '../lib/entry.js';
import {
  ageOn,
  countdown,
  dobText,
  formatDobInput,
  formatPhoneInput,
  isMobile,
  localDate,
  parseDob,
  whenLabel,
} from '../lib/format.js';
import { useSecondsLeft } from '../lib/hooks.js';
import { BOT_WAIT_MESSAGE, BotCheck, botTokenMissing, type TurnstileHandle } from './Turnstile.js';

/** Carriers most offices see; "Other" opens a free-text field. */
export const CARRIERS = [
  'Delta Dental',
  'MetLife',
  'Cigna',
  'Aetna',
  'Guardian',
  'United Concordia',
  'Humana',
  'Blue Cross Blue Shield',
  'Ameritas',
  'Principal',
] as const;

const HEARD = [
  'Google search',
  'Google Maps',
  'Friend or family',
  'My dentist referred me',
  'Facebook or Instagram',
  'Insurance directory',
  'Drove by',
  'Other',
];

export interface DetailsDraft {
  first: string;
  last: string;
  dob: string;
  mobile: string;
  email: string;
  bFirst: string;
  bLast: string;
  bMobile: string;
  bEmail: string;
  insurance: string;
  carrierOther: string;
  note: string;
  heard: string;
  sms: boolean;
  marketing: boolean;
}

export function emptyDraft(booker: Booker | null): DetailsDraft {
  return {
    first: '',
    last: booker?.lastName ?? '',
    dob: '',
    mobile: '',
    email: '',
    bFirst: booker?.firstName ?? '',
    bLast: booker?.lastName ?? '',
    bMobile: booker?.mobile ?? '',
    bEmail: booker?.email ?? '',
    insurance: '',
    carrierOther: '',
    note: '',
    heard: '',
    // Consents never start checked (OB-18), even for a second family member.
    sms: false,
    marketing: false,
  };
}

export type DraftErrors = Partial<Record<keyof DetailsDraft | 'insurance', string>>;

/** What an identity token already knows about the patient. */
export interface KnownPatient {
  firstName: string;
  lastName: string;
  birthdate: string | null;
  /** False when the record has no mobile: the visitor must give one. */
  hasMobile: boolean;
}

/** Client-side checks; the server repeats every one. */
export function validateDraft(
  d: DetailsDraft,
  o: {
    bookingFor: BookingFor;
    identified: boolean;
    today: string;
    visitDate: string;
    type: PublicType;
    known?: KnownPatient | null;
  },
): DraftErrors {
  const e: DraftErrors = {};
  if (o.identified && o.bookingFor === 'self' && o.known) {
    // Only what the record is missing.
    if (!o.known.hasMobile && !isMobile(d.mobile)) e.mobile = 'Enter a 10-digit mobile number.';
    if (!o.known.birthdate && !parseDob(d.dob, o.today)) e.dob = 'Enter a real date as MM/DD/YYYY.';
  }
  if (!(o.identified && o.bookingFor === 'self')) {
    if (!d.first.trim()) e.first = 'Enter the first name.';
    if (!d.last.trim()) e.last = 'Enter the last name.';
    const dob = parseDob(d.dob, o.today);
    if (!dob) e.dob = 'Enter a real date as MM/DD/YYYY.';
    else {
      const age = ageOn(dob, o.visitDate);
      if (o.type.ageBand === 'adult' && age < 18) e.dob = 'This visit is for adults 18 and over.';
      if (o.type.ageBand === 'child' && age >= 18) e.dob = 'This visit is for children.';
    }
    if (o.bookingFor === 'self') {
      if (!isMobile(d.mobile)) e.mobile = 'Enter a 10-digit mobile number.';
    } else if (d.mobile && !isMobile(d.mobile)) {
      e.mobile = 'Enter a 10-digit mobile number, or leave it empty.';
    }
    if (d.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email))
      e.email = 'Check the email address.';
  }
  if (o.bookingFor !== 'self') {
    if (!d.bFirst.trim()) e.bFirst = 'Enter your first name.';
    if (!d.bLast.trim()) e.bLast = 'Enter your last name.';
    if (!isMobile(d.bMobile)) e.bMobile = 'Enter a 10-digit mobile number.';
    if (d.bEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.bEmail))
      e.bEmail = 'Check the email address.';
  }
  if (d.insurance === 'Other' && !d.carrierOther.trim())
    e.carrierOther = 'Enter the insurance company.';
  return e;
}

/** The submit body for a valid draft. */
export function buildSubmit(
  d: DetailsDraft,
  o: {
    hold: Hold;
    visitorId: string;
    bookingFor: BookingFor;
    identityToken: string | null;
    attribution: Attribution;
    today: string;
    familyOf: string | null;
    turnstileToken: string | null;
    known?: KnownPatient | null;
    /** Version of the SMS opt-in wording shown (site.smsConsent). */
    disclosureVersion?: string | null;
  },
): SubmitRequest {
  const viaIdentity = o.identityToken !== null && o.bookingFor === 'self';
  const dob = parseDob(d.dob, o.today);
  // A record without a mobile (or birthdate) gets the typed one alongside the token.
  const fill =
    viaIdentity && o.known && (!o.known.hasMobile || !o.known.birthdate)
      ? {
          patient: {
            firstName: o.known.firstName,
            lastName: o.known.lastName,
            birthdate: (o.known.birthdate ?? dob) as string,
            mobile: d.mobile,
          },
        }
      : {};
  const patientMobile = d.mobile.trim() || (o.bookingFor !== 'self' ? d.bMobile : '');
  const carrier =
    d.insurance === 'Other' ? d.carrierOther.trim() : d.insurance === 'self_pay' ? '' : d.insurance;
  const attribution: Attribution = { ...o.attribution };
  if (!knowsSource(o.attribution) && d.heard) attribution.heardAbout = d.heard;
  return {
    holdId: o.hold.holdId,
    holdToken: o.hold.holdToken,
    visitorId: o.visitorId,
    bookingFor: o.bookingFor,
    ...(viaIdentity
      ? { identityToken: o.identityToken as string, ...fill }
      : {
          patient: {
            firstName: d.first.trim(),
            lastName: d.last.trim(),
            birthdate: dob as string,
            mobile: patientMobile,
            ...(d.email.trim() ? { email: d.email.trim() } : {}),
          },
        }),
    ...(o.bookingFor !== 'self'
      ? {
          booker: {
            firstName: d.bFirst.trim(),
            lastName: d.bLast.trim(),
            mobile: d.bMobile,
            ...(d.bEmail.trim() ? { email: d.bEmail.trim() } : {}),
          },
        }
      : {}),
    ...(d.insurance
      ? {
          insurance: {
            carrier: carrier || (d.insurance === 'self_pay' ? 'Self pay' : ''),
            selfPay: d.insurance === 'self_pay',
          },
        }
      : {}),
    ...(d.note.trim() ? { note: d.note.trim() } : {}),
    consents: {
      transactionalSms: d.sms,
      marketing: d.marketing,
      ...(o.disclosureVersion ? { disclosureVersion: o.disclosureVersion } : {}),
    },
    attribution,
    ...(o.familyOf ? { familyOf: o.familyOf } : {}),
    ...(o.turnstileToken ? { turnstileToken: o.turnstileToken } : {}),
  };
}

interface Props {
  api: BookingApi;
  site: PublicSite;
  type: PublicType;
  hold: Hold;
  bookingFor: BookingFor;
  identityToken: string | null;
  attribution: Attribution;
  visitorId: string;
  familyOf: string | null;
  draft: DetailsDraft;
  onDraft: (d: DetailsDraft) => void;
  onHeld: (h: Hold) => void;
  onHoldLost: (message: string) => void;
  onSubmitted: (r: SubmitResult, patientFirstName: string, booker: Booker | null) => void;
  onIdentityExpired: () => void;
  onBack: () => void;
}

export function DetailsStep(p: Props) {
  const { site, type, hold, draft: d } = p;
  const tz = site.timezone;
  const identified = site.identity !== null && p.identityToken !== null;
  const viaIdentity = identified && p.bookingFor === 'self';
  const known: KnownPatient | null = site.identity
    ? {
        firstName: site.identity.firstName,
        lastName: site.identity.lastName,
        birthdate: site.identity.birthdate,
        hasMobile: site.identity.mobileMasked !== null,
      }
    : null;
  const left = useSecondsLeft(hold.expiresAt);
  const expired = left !== null && left <= 0;
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alts, setAlts] = useState<Slot[]>([]);
  const [turnstile, setTurnstile] = useState<string | null>(null);
  const botWidget = useRef<TurnstileHandle>(null);
  const reported = useRef<string>('');

  const set = <K extends keyof DetailsDraft>(k: K, v: DetailsDraft[K]) =>
    p.onDraft({ ...d, [k]: v });
  const visitDate = localDate(hold.start, tz);
  const errors = validateDraft(d, {
    bookingFor: p.bookingFor,
    identified,
    today: site.today,
    visitDate,
    type,
    known,
  });
  const show = (k: keyof DraftErrors) => (tried ? errors[k] : undefined);

  // Abandonment lead (OB-19): once there is a reachable mobile with consent.
  const contactMobile = p.bookingFor === 'self' ? d.mobile : d.bMobile;
  const contactFirst = p.bookingFor === 'self' ? d.first : d.bFirst;
  useEffect(() => {
    if (viaIdentity || !isMobile(contactMobile) || !d.sms) return;
    const sig = `${contactMobile}|${contactFirst.trim()}`;
    if (reported.current === sig) return;
    const id = window.setTimeout(() => {
      reported.current = sig;
      p.api
        .progress({
          visitorId: p.visitorId,
          step: 'details',
          typeId: type.id,
          slotStart: hold.start,
          mobile: contactMobile,
          ...(contactFirst.trim() ? { firstName: contactFirst.trim().slice(0, 60) } : {}),
          transactionalConsent: true,
          attribution: p.attribution,
        })
        .catch(() => undefined);
    }, 800);
    return () => window.clearTimeout(id);
  }, [
    viaIdentity,
    contactMobile,
    contactFirst,
    d.sms,
    p.api,
    p.visitorId,
    type.id,
    hold.start,
    p.attribution,
  ]);

  const rehold = async (slot: { start: string; providerId: string }) => {
    setBusy(true);
    setError(null);
    setAlts([]);
    try {
      p.onHeld(
        await p.api.hold({
          typeId: type.id,
          providerId: slot.providerId,
          start: slot.start,
          visitorId: p.visitorId,
          replace: { holdId: hold.holdId, holdToken: hold.holdToken },
        }),
      );
    } catch (err) {
      const a = alternativesOf(err);
      if (a.length === 0) p.onHoldLost(messageOf(err));
      else {
        setError(messageOf(err));
        setAlts(a);
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (Object.keys(errors).length > 0) {
      const first = document.querySelector<HTMLElement>('.bw-details [aria-invalid="true"]');
      first?.focus();
      return;
    }
    if (botTokenMissing(site.turnstile, turnstile)) {
      setError(BOT_WAIT_MESSAGE);
      return;
    }
    setBusy(true);
    setError(null);
    setAlts([]);
    try {
      const body = buildSubmit(d, {
        hold,
        visitorId: p.visitorId,
        bookingFor: p.bookingFor,
        identityToken: p.identityToken,
        attribution: p.attribution,
        today: site.today,
        familyOf: p.familyOf,
        turnstileToken: turnstile,
        known,
        disclosureVersion: site.smsConsent?.version ?? null,
      });
      const result = await p.api.submit(body);
      const patientFirst = viaIdentity ? (site.identity?.firstName ?? '') : d.first.trim();
      const booker: Booker =
        p.bookingFor === 'self'
          ? {
              firstName: viaIdentity ? (site.identity?.firstName ?? '') : d.first.trim(),
              lastName: viaIdentity ? (site.identity?.lastName ?? '') : d.last.trim(),
              mobile: viaIdentity ? '' : d.mobile,
              email: viaIdentity ? '' : d.email.trim(),
            }
          : {
              firstName: d.bFirst.trim(),
              lastName: d.bLast.trim(),
              mobile: d.bMobile,
              email: d.bEmail.trim(),
            };
      p.onSubmitted(result, patientFirst, booker);
    } catch (err) {
      const reason = reasonOf(err);
      if (reason === 'identity_expired') p.onIdentityExpired();
      setError(messageOf(err));
      setAlts(alternativesOf(err));
      // Each token works once: solve again before the next try.
      if (turnstile) botWidget.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  const tone = expired ? 'danger' : left !== null && left < 60 ? 'warning' : 'info';

  return (
    <form
      className="bw-step bw-details"
      data-testid="step-details"
      onSubmit={(e) => void submit(e)}
      noValidate
    >
      <div className="bw-hold" data-tone={tone} role="status" aria-live="polite" data-testid="hold">
        <Clock size={18} aria-hidden />
        <div className="bw-hold__text">
          <strong>{whenLabel(hold.start, tz, site.today)}</strong>
          <span className="bw-dim">
            {type.label}
            {site.providers.find((x) => x.id === hold.providerId)
              ? ` with ${site.providers.find((x) => x.id === hold.providerId)?.displayName}`
              : ''}
          </span>
        </div>
        {expired ? (
          <Button
            type="button"
            size="sm"
            variant="primary"
            loading={busy}
            onClick={() => void rehold({ start: hold.start, providerId: hold.providerId })}
          >
            Hold again
          </Button>
        ) : (
          <span
            className="bw-hold__timer vel-num"
            aria-label={`Held for ${countdown(left ?? 0)} more`}
          >
            Held {countdown(left ?? 0)}
          </span>
        )}
      </div>
      {expired ? (
        <p className="bw-dim bw-hold__note">
          Your hold on this time ran out. Hold it again if it is still open, or{' '}
          <button type="button" className="bw-link" onClick={p.onBack}>
            pick another time
          </button>
          .
        </p>
      ) : null}

      {viaIdentity && site.identity ? (
        <section className="bw-section bw-who" aria-label="Patient">
          <ShieldCheck size={18} aria-hidden />
          <div>
            <p>
              <strong>
                {site.identity.firstName} {site.identity.lastName}
              </strong>
              {site.identity.birthdate ? (
                <span className="bw-dim"> · born {dobText(site.identity.birthdate)}</span>
              ) : null}
            </p>
            <p className="bw-dim">
              {site.identity.mobileMasked
                ? `Confirmation goes to ${site.identity.mobileMasked}.`
                : 'We do not have a mobile number for you. Add one so we can text about this visit.'}
            </p>
            {!site.identity.mobileMasked || !site.identity.birthdate ? (
              <div className="bw-grid2 bw-who__fill">
                {!site.identity.birthdate ? (
                  <Field label="Date of birth" required error={show('dob')}>
                    <Input
                      inputMode="numeric"
                      autoComplete="bday"
                      placeholder="MM/DD/YYYY"
                      value={d.dob}
                      onChange={(e) => set('dob', formatDobInput(e.target.value))}
                      invalid={!!show('dob')}
                    />
                  </Field>
                ) : null}
                {!site.identity.mobileMasked ? (
                  <Field label="Mobile number" required error={show('mobile')}>
                    <Input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel-national"
                      placeholder="(555) 555-0100"
                      value={d.mobile}
                      onChange={(e) => set('mobile', formatPhoneInput(e.target.value))}
                      invalid={!!show('mobile')}
                    />
                  </Field>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      ) : (
        <section className="bw-section" aria-labelledby="bw-patient">
          <h2 id="bw-patient" className="bw-q">
            {p.bookingFor === 'self' ? 'Your details' : 'Patient details'}
          </h2>
          <div className="bw-grid2">
            <Field label="First name" required error={show('first')}>
              <Input
                autoComplete={p.bookingFor === 'self' ? 'given-name' : 'off'}
                value={d.first}
                onChange={(e) => set('first', e.target.value)}
                maxLength={60}
                invalid={!!show('first')}
              />
            </Field>
            <Field label="Last name" required error={show('last')}>
              <Input
                autoComplete={p.bookingFor === 'self' ? 'family-name' : 'off'}
                value={d.last}
                onChange={(e) => set('last', e.target.value)}
                maxLength={60}
                invalid={!!show('last')}
              />
            </Field>
          </div>
          <div className="bw-grid2">
            <Field label="Date of birth" required error={show('dob')}>
              <Input
                inputMode="numeric"
                autoComplete={p.bookingFor === 'self' ? 'bday' : 'off'}
                placeholder="MM/DD/YYYY"
                value={d.dob}
                onChange={(e) => set('dob', formatDobInput(e.target.value))}
                invalid={!!show('dob')}
              />
            </Field>
            <Field
              label={p.bookingFor === 'self' ? 'Mobile number' : 'Patient mobile'}
              required={p.bookingFor === 'self'}
              error={show('mobile')}
              hint={p.bookingFor === 'self' ? undefined : 'Optional. We use yours if empty.'}
            >
              <Input
                type="tel"
                inputMode="tel"
                autoComplete={p.bookingFor === 'self' ? 'tel-national' : 'off'}
                placeholder="(555) 555-0100"
                value={d.mobile}
                onChange={(e) => set('mobile', formatPhoneInput(e.target.value))}
                invalid={!!show('mobile')}
              />
            </Field>
          </div>
          <Field label="Email" hint="Optional" error={show('email')}>
            <Input
              type="email"
              inputMode="email"
              autoComplete={p.bookingFor === 'self' ? 'email' : 'off'}
              value={d.email}
              onChange={(e) => set('email', e.target.value)}
              maxLength={200}
              invalid={!!show('email')}
            />
          </Field>
        </section>
      )}

      {p.bookingFor !== 'self' ? (
        <section className="bw-section" aria-labelledby="bw-booker">
          <h2 id="bw-booker" className="bw-q">
            Your details
          </h2>
          <p className="bw-dim bw-q__lead">We text you, not the patient, about this visit.</p>
          <div className="bw-grid2">
            <Field label="Your first name" required error={show('bFirst')}>
              <Input
                autoComplete="given-name"
                value={d.bFirst}
                onChange={(e) => set('bFirst', e.target.value)}
                maxLength={60}
                invalid={!!show('bFirst')}
              />
            </Field>
            <Field label="Your last name" required error={show('bLast')}>
              <Input
                autoComplete="family-name"
                value={d.bLast}
                onChange={(e) => set('bLast', e.target.value)}
                maxLength={60}
                invalid={!!show('bLast')}
              />
            </Field>
          </div>
          <div className="bw-grid2">
            <Field label="Your mobile" required error={show('bMobile')}>
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="(555) 555-0100"
                value={d.bMobile}
                onChange={(e) => set('bMobile', formatPhoneInput(e.target.value))}
                invalid={!!show('bMobile')}
              />
            </Field>
            <Field label="Your email" hint="Optional" error={show('bEmail')}>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={d.bEmail}
                onChange={(e) => set('bEmail', e.target.value)}
                maxLength={200}
                invalid={!!show('bEmail')}
              />
            </Field>
          </div>
        </section>
      ) : null}

      <section className="bw-section" aria-labelledby="bw-ins">
        <h2 id="bw-ins" className="bw-q">
          Dental insurance
        </h2>
        <div className="bw-grid2">
          <Field label="Insurance" hint="Optional. You can bring your card to the visit.">
            <Combobox
              value={d.insurance || null}
              onChange={(v) => set('insurance', v ?? '')}
              clearable
              placeholder="Choose your insurance"
              options={[
                ...CARRIERS.map((c) => ({ value: c, label: c })),
                { value: 'Other', label: 'Other insurance' },
                { value: 'self_pay', label: 'No insurance, I will pay myself' },
              ]}
            />
          </Field>
          {d.insurance === 'Other' ? (
            <Field label="Insurance company" required error={show('carrierOther')}>
              <Input
                value={d.carrierOther}
                onChange={(e) => set('carrierOther', e.target.value)}
                maxLength={80}
                invalid={!!show('carrierOther')}
              />
            </Field>
          ) : null}
        </div>
      </section>

      <section className="bw-section" aria-labelledby="bw-more">
        <h2 id="bw-more" className="bw-q">
          Anything we should know?
        </h2>
        <Field
          label="Note for the office"
          hideLabel
          hint="Optional. Please do not include card, insurance or Social Security numbers."
        >
          <Textarea
            value={d.note}
            onChange={(e) => set('note', e.target.value)}
            maxLength={500}
            rows={3}
          />
        </Field>
        {!knowsSource(p.attribution) ? (
          <Field label="How did you hear about us?" hint="Optional">
            <Select
              {...(d.heard ? { value: d.heard } : {})}
              onValueChange={(v) => set('heard', v)}
              placeholder="Choose one"
              options={HEARD.map((h) => ({ value: h, label: h }))}
            />
          </Field>
        ) : null}
      </section>

      <section className="bw-section bw-consents" aria-label="Messages">
        <Checkbox
          checked={d.sms}
          onCheckedChange={(v) => set('sms', v === true)}
          label={
            site.smsConsent?.transactional.label ??
            `Text me appointment confirmations and reminders from ${site.practiceName}`
          }
          description={
            site.smsConsent?.transactional.text ??
            'Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for help.'
          }
        />
        <Checkbox
          checked={d.marketing}
          onCheckedChange={(v) => set('marketing', v === true)}
          label={
            site.smsConsent?.marketing.label ??
            `Send me occasional news and offers from ${site.practiceName}`
          }
          description={site.smsConsent?.marketing.text ?? 'Optional. Unsubscribe at any time.'}
        />
        {site.smsConsent ? (
          <p className="bw-consent-links">
            <a href={site.smsConsent.termsUrl} target="_blank" rel="noopener">
              SMS terms
            </a>
            <span aria-hidden="true"> · </span>
            <a href={site.smsConsent.privacyUrl} target="_blank" rel="noopener">
              Privacy policy
            </a>
          </p>
        ) : null}
      </section>

      <BotCheck
        ref={botWidget}
        bot={site.turnstile}
        action={TURNSTILE_ACTIONS.bookingRequest}
        onToken={setTurnstile}
      />

      {error ? (
        <div className="bw-banner" data-tone="danger" role="alert" data-testid="submit-error">
          <AlertTriangle size={18} aria-hidden />
          <div>
            <p>{error}</p>
            {alts.length > 0 ? (
              <div className="bw-alts">
                {alts.map((s) => (
                  <Button
                    key={s.start + s.providerId}
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => void rehold(s)}
                  >
                    {whenLabel(s.start, tz, site.today)}
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="bw-actions">
        <Button type="button" variant="ghost" size="lg" onClick={p.onBack}>
          Back
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy}
          disabled={expired}
          data-testid="submit"
        >
          Book this time
        </Button>
      </div>
      <p className="bw-fine">
        Times are held for five minutes while you finish. Your details go only to {site.officeName}.
      </p>
    </form>
  );
}
